/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import Request, {Metadata, Notification, UNSMetadata} from './model/Request';
import Utils from './Utils';
import ProductMapping, {defaultProductName} from './model/ProductMapping';
import Configuration, {AuthInfo} from './model/Configuration';
import UNSResponse from './model/UNSResponse';
import Response from './model/Response';
import {PulseService} from './PulseService';
import PulseUNSConfig from './config/PulseUNSConfig.json';

const SUCCESS_RESPONSE_CODE = 202;
const FAILURE_RESPONSE_CODE = 400;

export default class PulseUNSService extends PulseService {
  public pulseEndpoint: string;
  public authInfo: AuthInfo;

  /**
   * @description Constructor for UserService class.
   *
   * @param {Object} configuration -  Config to initialize Pulse service.
   */
  public constructor(configuration: Configuration) {
    super();
    this.authInfo = configuration.authInfo;
    this.pulseEndpoint = PulseUNSConfig.endpoints[configuration.env];
  }

  public async sendNotifications(request: Request): Promise<Response> {
    const imsOrgId = request.imsOrgId;
    const method = request.method;
    const appId = request.appId;
    const returnResponse: Response = {notifications: {notification: []}};
    const that = this;

    const {error} = Utils.validateBaseParams(request.data, imsOrgId, method);

    if (error) {
      returnResponse.notifications.notification.push({
        statusCode: FAILURE_RESPONSE_CODE,
        errorMessage: error,
        'notification-id': ''
      });
      return new Promise(async resolve => {
        resolve(returnResponse);
      });
    }

    const requests = request.data.map(element => {
      const {error} = Utils.validateUNSRequest(element, method);
      if (error) {
        return new Promise(async reject => {
          reject({
            statusCode: FAILURE_RESPONSE_CODE,
            errorMessage: error,
            'notification-id': ''
          });
        });
      }
      const {opts, url} = that.processUNSParams(element, imsOrgId, method, appId);
      return PulseUNSService.createNotification(url, opts);
    });

    const results: any = await Promise.all(requests);
    return new Promise(async resolve => {
      returnResponse.notifications.notification = results;
      resolve(returnResponse);
    });
  }

  private static async createNotification(url: string, opts: RequestInit): Promise<UNSResponse> {
    const response = await fetch(url, opts);
    const returnResponse: UNSResponse = {errorMessage: '', statusCode: 0};
    return new Promise(async resolve => {
      try {
        const unsResponse: any = await response.json();
        if (unsResponse['error-code']) {
          returnResponse.statusCode = unsResponse['error-code'];
          returnResponse.errorMessage = <string>unsResponse['error-message'];
          returnResponse['notification-id'] = '';
        } else {
          returnResponse.statusCode = SUCCESS_RESPONSE_CODE;
          returnResponse.errorMessage = '';
          returnResponse['notification-id'] = unsResponse['notification-id'];
        }
        resolve(returnResponse);
      } catch (error) {
        returnResponse.statusCode = FAILURE_RESPONSE_CODE;
        returnResponse.errorMessage = error;
        returnResponse['notification-id'] = '';
        resolve(returnResponse);
      }
    });
  }

  private processUNSParams(element: Notification, imsOrgId: string, method: string, appId: string): any {
    let metadata: Metadata | undefined = element.metadata;
    if (!metadata) {
      metadata = {};
    }
    let productName: string | undefined = ProductMapping.get(appId.toLowerCase());
    if (!productName) {
      productName = defaultProductName;
    }
    metadata['product'] = productName;
    const unsMetadata: UNSMetadata = Object.assign(Utils.constructUNSMetadata(metadata));
    const notification: any = {
      payload: element.templateParams,
      metadata: unsMetadata
    };

    if (element.userIds && element.userIds.length > 0) {
      notification['user-ids'] = element.userIds;
    } else if (element.groupIds && element.groupIds.length > 0) {
      notification['group-ids'] = element.groupIds;
    } else if (element.sendToOrg) {
      notification['sendToOrg'] = element.sendToOrg;
    }

    const opts: RequestInit = Object.assign({
      headers: Utils.getUNSDefaultHeaders(this.authInfo.accessToken, imsOrgId),
      method,
      body: JSON.stringify(notification)
    });

    const url = Utils.getUrlFromNotificationType(this.pulseEndpoint, element.messageType);
    return {opts, url: url.href};
  }
}
